import PropTypes from 'prop-types';

import useDocumentHead from '@/hooks/useDocumentHead';

/**
 * SEO Component for managing page-specific meta tags and structured data.
 * Applies the tags through useDocumentHead, which writes them to the document
 * head directly. Renders nothing.
 *
 * Automatically handles:
 * - noindex for development/staging environments (via REACT_APP_NOINDEX)
 * - Canonical URLs pointing to production domain (sustainabilityhub.co)
 * - Open Graph and Twitter Card meta tags
 * - JSON-LD structured data: BreadcrumbList, WebSite, Organization
 *
 * Props:
 * - breadcrumbs: Array of {name, path} for BreadcrumbList schema (enables
 *   Google sitelinks and breadcrumb display in search results)
 * - isHomePage: When true, emits WebSite + Organization schemas (enables
 *   Google Knowledge Panel and Sitelinks Search Box)
 * - jsonLd: Additional page-specific JSON-LD object to emit
 */

const PRODUCTION_URL = 'https://sustainabilityhub.co';
const SITE_TITLE = 'Sustainability Hub';
const DEFAULT_DESCRIPTION =
    'Empowering communities with data-driven insights for environmental sustainability, economic prosperity, and well-being.';
const DEFAULT_IMAGE = `${PRODUCTION_URL}/shLogoVectors.png`;

const SEO = ({
    title,
    description,
    canonical,
    image,
    type = 'website',
    noindex = false,
    breadcrumbs,
    isHomePage = false,
    jsonLd,
}) => {
    const shouldNoindex = noindex || process.env.REACT_APP_NOINDEX === 'true';

    const fullTitle = title
        ? `${title} | ${SITE_TITLE}`
        : `${SITE_TITLE} - Data-Driven Sustainability Insights`;
    const metaDescription = description || DEFAULT_DESCRIPTION;
    const metaImage = image || DEFAULT_IMAGE;

    const canonicalUrl = canonical
        ? `${PRODUCTION_URL}${canonical}`
        : typeof window !== 'undefined'
          ? `${PRODUCTION_URL}${window.location.pathname}`
          : PRODUCTION_URL;

    // --- JSON-LD Structured Data ---
    const structuredData = [];

    // BreadcrumbList — enables breadcrumb display in Google search results
    if (breadcrumbs?.length) {
        structuredData.push({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: crumb.name,
                item: `${PRODUCTION_URL}${crumb.path}`,
            })),
        });
    }

    // WebSite — enables Sitelinks Search Box in Google results
    if (isHomePage) {
        structuredData.push({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_TITLE,
            url: PRODUCTION_URL,
            description: DEFAULT_DESCRIPTION,
            potentialAction: {
                '@type': 'SearchAction',
                target: {
                    '@type': 'EntryPoint',
                    urlTemplate: `${PRODUCTION_URL}/data?q={search_term_string}`,
                },
                'query-input': 'required name=search_term_string',
            },
        });

        // Organization — establishes identity for Knowledge Panel
        structuredData.push({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: SITE_TITLE,
            url: PRODUCTION_URL,
            logo: DEFAULT_IMAGE,
            description: DEFAULT_DESCRIPTION,
            foundingDate: '2024',
            founder: {
                '@type': 'CollegeOrUniversity',
                name: 'Metropolitan State University of Denver',
                url: 'https://www.msudenver.edu',
            },
            sameAs: ['https://www.msudenver.edu'],
        });
    }

    // Page-specific structured data
    if (jsonLd) {
        structuredData.push(jsonLd);
    }

    useDocumentHead({
        title: fullTitle,
        description: metaDescription,
        canonicalUrl,
        image: metaImage,
        type,
        noindex: shouldNoindex,
        structuredData,
    });

    // Renders nothing: the head is written as an effect rather than as elements
    // React must hoist. Keeping the component (instead of calling the hook at
    // each page) preserves every existing call site and the props contract.
    return null;
};

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    canonical: PropTypes.string,
    image: PropTypes.string,
    type: PropTypes.string,
    noindex: PropTypes.bool,
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
        })
    ),
    isHomePage: PropTypes.bool,
    jsonLd: PropTypes.object,
};

export default SEO;
